import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogOut, Plus, Users, Phone, Mail, MapPin, X, Send, Loader2,
  MessageSquare, Trash2, Flame, Siren, UserPlus, Search,
  CalendarDays, CalendarPlus, Smartphone, Copy, Clock, Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STAGES = {
  new: { label: "New", color: "#60a5fa", bg: "rgba(96,165,250,0.12)" },
  contacted: { label: "Contacted", color: "#a3a3a3", bg: "rgba(163,163,163,0.12)" },
  hot_lead: { label: "Hot Lead", color: "#f59e0b", bg: "rgba(245,158,11,0.14)" },
  emergency: { label: "Emergency", color: "#ef4444", bg: "rgba(239,68,68,0.16)" },
};
const STAGE_KEYS = Object.keys(STAGES);
const input = "w-full bg-[#0d0d0d] border border-white/10 px-4 py-3 text-[#F5F5F0] placeholder:text-[#5a5a5a] focus:outline-none focus:border-gold transition-colors duration-300";

function StageBadge({ status, onClick }) {
  const s = STAGES[status] || STAGES.new;
  return (
    <span onClick={onClick} className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-mono uppercase tracking-wider cursor-default"
      style={{ color: s.color, background: s.bg, border: `1px solid ${s.color}33` }}>
      {status === "hot_lead" && <Flame className="h-3 w-3" />}
      {status === "emergency" && <Siren className="h-3 w-3" />}
      {s.label}
    </span>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showTeam, setShowTeam] = useState(false);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("leads");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/crm/leads");
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/login");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const counts = STAGE_KEYS.reduce((a, k) => ({ ...a, [k]: leads.filter((l) => l.status === k).length }), {});
  const filtered = leads.filter((l) => {
    if (filter !== "all" && l.status !== filter) return false;
    if (q && !(`${l.name} ${l.phone} ${l.email} ${l.service}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F0] font-body">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="247 Aftermath" className="h-10 w-auto" />
            <span className="hidden sm:inline font-mono text-[11px] tracking-widest uppercase text-gold">CRM</span>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <button onClick={() => setShowTeam(true)} className="inline-flex items-center gap-2 border border-white/10 px-4 py-2 text-sm hover:border-gold hover:text-gold transition-colors" data-testid="team-btn">
                <Users className="h-4 w-4" /> <span className="hidden sm:inline">Team</span>
              </button>
            )}
            <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 bg-gold text-black font-semibold px-4 py-2 text-sm hover:bg-gold-light transition-colors" data-testid="add-contact-btn">
              <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Add Contact</span>
            </button>
            <button onClick={doLogout} className="inline-flex items-center gap-2 border border-white/10 px-3 py-2 text-sm hover:border-red-500 hover:text-red-400 transition-colors" data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-2 mb-8">
          <button onClick={() => setView("leads")} data-testid="view-leads"
            className="px-5 py-2.5 text-sm font-mono uppercase tracking-wider border transition-colors"
            style={view === "leads" ? { background: "#D4AF37", color: "#050505", borderColor: "#D4AF37" } : { borderColor: "rgba(255,255,255,0.1)", color: "#A3A3A3" }}>
            Leads
          </button>
          <button onClick={() => setView("appointments")} data-testid="view-appointments"
            className="px-5 py-2.5 text-sm font-mono uppercase tracking-wider border transition-colors"
            style={view === "appointments" ? { background: "#D4AF37", color: "#050505", borderColor: "#D4AF37" } : { borderColor: "rgba(255,255,255,0.1)", color: "#A3A3A3" }}>
            Appointments
          </button>
        </div>

        {view === "leads" && (<>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-4xl">Leads</h1>
            <p className="text-[#A3A3A3] text-sm mt-1">Signed in as {user?.name || user?.email} · {user?.role}</p>
          </div>
          <div className="relative w-full lg:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5a5a5a]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, service…" className={`${input} pl-10`} data-testid="lead-search" />
          </div>
        </div>

        {/* Stage filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="All" count={leads.length} color="#D4AF37" testid="filter-all" />
          {STAGE_KEYS.map((k) => (
            <FilterChip key={k} active={filter === k} onClick={() => setFilter(k)} label={STAGES[k].label} count={counts[k]} color={STAGES[k].color} testid={`filter-${k}`} />
          ))}
        </div>

        {/* Leads list */}
        {loading ? (
          <div className="py-20 text-center text-[#A3A3A3]"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center border border-white/10 bg-[#0a0a0a]">
            <p className="text-[#A3A3A3]">No leads here yet.</p>
          </div>
        ) : (
          <div className="border border-white/10 divide-y divide-white/10" data-testid="leads-list">
            {filtered.map((l) => (
              <button key={l.id} onClick={() => setSelected(l)} className="w-full text-left p-4 sm:p-5 hover:bg-white/[0.03] transition-colors flex items-center justify-between gap-4" data-testid={`lead-row-${l.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-display text-lg truncate">{l.name}</span>
                    {l.source === "manual" && <span className="text-[10px] font-mono uppercase text-[#5a5a5a] border border-white/10 px-2 py-0.5">manual</span>}
                    {(l.source === "ai_chat") && <span className="text-[10px] font-mono uppercase text-gold border border-gold/30 px-2 py-0.5">AI chat</span>}
                    {l.ai_priority && <AiBadge priority={l.ai_priority} />}
                    {l.sms_opt_out && <span className="text-[10px] font-mono uppercase text-red-400 border border-red-400/40 px-2 py-0.5" data-testid={`lead-optout-${l.id}`}>SMS opted out</span>}
                  </div>
                  <div className="text-sm text-[#A3A3A3] mt-1 flex flex-wrap gap-x-4 gap-y-1">
                    <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3 text-gold" /> {l.phone || "—"}</span>
                    {l.service && <span>{l.service}</span>}
                  </div>
                  {l.ai_summary && <p className="text-xs text-[#8a8a8a] mt-1.5 flex items-start gap-1.5" data-testid={`lead-ai-summary-${l.id}`}><Sparkles className="h-3 w-3 text-gold mt-0.5 shrink-0" /> {l.ai_summary}</p>}
                </div>
                <StageBadge status={l.status} />
              </button>
            ))}
          </div>
        )}
        </>)}

        {view === "appointments" && <Appointments />}
      </div>

      <AnimatePresence>
        {selected && <LeadDrawer lead={selected} onClose={() => setSelected(null)} onChange={load} me={user} />}
      </AnimatePresence>
      <AnimatePresence>
        {showAdd && <AddContactModal onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); load(); }} />}
      </AnimatePresence>
      <AnimatePresence>
        {showTeam && <TeamModal onClose={() => setShowTeam(false)} me={user} />}
      </AnimatePresence>
    </div>
  );
}

function FilterChip({ active, onClick, label, count, color, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-mono uppercase tracking-wider border transition-colors"
      style={active ? { color: "#050505", background: color, borderColor: color } : { color: color, borderColor: `${color}44`, background: "transparent" }}>
      {label} <span className="opacity-70">{count}</span>
    </button>
  );
}

function Overlay({ children, onClose }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" style={{ overflowY: "auto" }}>
      {children}
    </motion.div>
  );
}

function LeadDrawer({ lead, onClose, onChange, me }) {
  const [current, setCurrent] = useState(lead);
  const [notes, setNotes] = useState(lead.notes || "");
  const [messages, setMessages] = useState([]);
  const [channel, setChannel] = useState("sms");
  const [subject, setSubject] = useState("A message from 247 Aftermath Restoration");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);

  const loadMsgs = useCallback(async () => {
    const { data } = await api.get(`/crm/leads/${lead.id}/messages`);
    setMessages(data);
  }, [lead.id]);
  useEffect(() => {
    loadMsgs();
    const t = setInterval(loadMsgs, 15000);
    return () => clearInterval(t);
  }, [loadMsgs]);

  const setStatus = async (status) => {
    const { data } = await api.patch(`/crm/leads/${lead.id}`, { status });
    setCurrent(data); onChange();
    toast.success(`Moved to ${STAGES[status].label}`);
  };
  const saveNotes = async () => {
    await api.patch(`/crm/leads/${lead.id}`, { notes });
    toast.success("Notes saved");
  };
  const del = async () => {
    await api.delete(`/crm/leads/${lead.id}`);
    toast.success("Lead deleted"); onChange(); onClose();
  };
  const send = async () => {
    if (!body.trim()) { toast.error("Write a message first"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/crm/leads/${lead.id}/message`, { channel, body, subject });
      if (data.warnings?.length) toast.warning(data.warnings.join("; "));
      else toast.success("Message sent");
      setBody(""); loadMsgs();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Send failed");
    } finally { setSending(false); }
  };
  const aiDraft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/crm/leads/${lead.id}/ai-draft`, { channel });
      setBody(data.draft || "");
      toast.success("Draft ready — edit and send");
    } catch (e) {
      toast.error("Could not generate a draft");
    } finally { setDrafting(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <motion.div initial={{ x: 60, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 60, opacity: 0 }} transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()} className="w-full max-w-xl min-h-screen bg-[#0a0a0a] border-l border-white/10 p-6 sm:p-8" data-testid="lead-drawer">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-display text-3xl">{current.name}</h2>
            <div className="mt-2"><StageBadge status={current.status} /></div>
          </div>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white p-1" data-testid="drawer-close"><X className="h-6 w-6" /></button>
        </div>

        <div className="mt-6 space-y-3 text-sm">
          <a href={`tel:${current.phone}`} className="flex items-center gap-3 text-[#F5F5F0] hover:text-gold"><Phone className="h-4 w-4 text-gold" /> {current.phone || "—"}</a>
          <a href={`mailto:${current.email}`} className="flex items-center gap-3 text-[#F5F5F0] hover:text-gold"><Mail className="h-4 w-4 text-gold" /> {current.email || "—"}</a>
          {current.address && <div className="flex items-center gap-3 text-[#A3A3A3]"><MapPin className="h-4 w-4 text-gold" /> {current.address}</div>}
          {current.service && <div className="text-[#A3A3A3]">Service: <span className="text-[#F5F5F0]">{current.service}</span></div>}
          {current.message && <div className="text-[#A3A3A3] border border-white/10 p-3 bg-black/40">{current.message}</div>}
        </div>

        {/* Stage changer */}
        <div className="mt-6">
          <p className="font-mono text-[11px] tracking-widest uppercase text-[#A3A3A3] mb-2">Move to stage</p>
          <div className="flex flex-wrap gap-2">
            {STAGE_KEYS.map((k) => (
              <button key={k} onClick={() => setStatus(k)} data-testid={`set-stage-${k}`}
                className="px-3 py-2 text-xs font-mono uppercase tracking-wider border transition-colors"
                style={current.status === k ? { color: "#050505", background: STAGES[k].color, borderColor: STAGES[k].color } : { color: STAGES[k].color, borderColor: `${STAGES[k].color}44` }}>
                {STAGES[k].label}
              </button>
            ))}
          </div>
        </div>

        {/* Message composer */}
        <div className="mt-8 border border-white/10 p-4 bg-black/30">
          <p className="font-mono text-[11px] tracking-widest uppercase text-gold mb-3 flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Message this contact</p>
          <div className="flex gap-2 mb-3">
            {["sms", "email", "both"].map((c) => (
              <button key={c} onClick={() => setChannel(c)} data-testid={`channel-${c}`}
                className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors"
                style={channel === c ? { color: "#050505", background: "#D4AF37", borderColor: "#D4AF37" } : { color: "#A3A3A3", borderColor: "rgba(255,255,255,0.1)" }}>
                {c === "sms" ? "Text" : c === "email" ? "Email" : "Both"}
              </button>
            ))}
          </div>
          {channel !== "sms" && (
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject" className={`${input} mb-2`} data-testid="msg-subject" />
          )}
          <div className="flex items-center justify-between mb-2">
            <p className="font-mono text-[10px] tracking-widest uppercase text-[#5a5a5a]">Message body</p>
            <button onClick={aiDraft} disabled={drafting} className="inline-flex items-center gap-1.5 text-xs text-gold hover:text-gold-light disabled:opacity-60" data-testid="ai-draft-btn">
              {drafting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} AI draft
            </button>
          </div>
          <textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type your message…" className={`${input} resize-none`} data-testid="msg-body" />
          <button onClick={send} disabled={sending} className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold py-3 hover:bg-gold-light transition-colors disabled:opacity-60" data-testid="msg-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
          </button>

          {messages.length > 0 && (
            <div className="mt-5 space-y-3 max-h-56 overflow-y-auto" data-testid="message-history">
              {messages.map((m) => {
                const inbound = m.direction === "inbound";
                return (
                  <div key={m.id} className={`border p-3 ${inbound ? "border-gold/30 bg-gold/5 mr-6" : "border-white/10 bg-[#0d0d0d] ml-6"}`} data-testid={`message-${m.direction || "outbound"}`}>
                    <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-[#5a5a5a]">
                      <span className={inbound ? "text-gold" : "text-[#A3A3A3]"}>{inbound ? "↙ Received" : "↗ Sent"} · {m.channel}</span>
                      <span>{new Date(m.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-[#F5F5F0] mt-1 whitespace-pre-wrap">{m.body}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="mt-6">
          <p className="font-mono text-[11px] tracking-widest uppercase text-[#A3A3A3] mb-2">Internal notes</p>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes} placeholder="Add notes about this lead…" className={`${input} resize-none`} data-testid="lead-notes" />
        </div>

        <button onClick={del} className="mt-8 inline-flex items-center gap-2 text-sm text-red-400 hover:text-red-300" data-testid="delete-lead">
          <Trash2 className="h-4 w-4" /> Delete lead
        </button>
      </motion.div>
    </Overlay>
  );
}

function CenterModal({ children, onClose }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
      <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
        onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[#0a0a0a] border border-white/10 p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
        {children}
      </motion.div>
    </motion.div>
  );
}

function AddContactModal({ onClose, onAdded }) {
  const [f, setF] = useState({ name: "", phone: "", email: "", service: "", address: "", message: "", status: "new" });
  const [saving, setSaving] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.name || !f.phone) { toast.error("Name and phone are required"); return; }
    setSaving(true);
    try { await api.post("/crm/leads", f); toast.success("Contact added"); onAdded(); }
    catch (e) { toast.error("Could not add contact"); }
    finally { setSaving(false); }
  };
  return (
    <CenterModal onClose={onClose}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl flex items-center gap-2"><UserPlus className="h-5 w-5 text-gold" /> Add Contact</h2>
        <button onClick={onClose} className="text-[#A3A3A3] hover:text-white"><X className="h-5 w-5" /></button>
      </div>
      <div className="space-y-3" data-testid="add-contact-form">
        <input className={input} placeholder="Full name *" value={f.name} onChange={up("name")} data-testid="add-name" />
        <input className={input} placeholder="Phone * (+1 708 555 0000)" value={f.phone} onChange={up("phone")} data-testid="add-phone" />
        <input className={input} placeholder="Email" value={f.email} onChange={up("email")} data-testid="add-email" />
        <input className={input} placeholder="Service needed" value={f.service} onChange={up("service")} data-testid="add-service" />
        <input className={input} placeholder="Property address" value={f.address} onChange={up("address")} data-testid="add-address" />
        <select className={`${input} cursor-pointer`} value={f.status} onChange={up("status")} data-testid="add-status">
          {STAGE_KEYS.map((k) => <option key={k} value={k} className="bg-[#0d0d0d]">{STAGES[k].label}</option>)}
        </select>
        <textarea rows={3} className={`${input} resize-none`} placeholder="Notes / details" value={f.message} onChange={up("message")} data-testid="add-message" />
        <button onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold py-3 hover:bg-gold-light transition-colors disabled:opacity-60" data-testid="add-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add Contact
        </button>
      </div>
    </CenterModal>
  );
}

function TeamModal({ onClose, me }) {
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "agent" });
  const [saving, setSaving] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const load = useCallback(async () => { const { data } = await api.get("/crm/users"); setUsers(data); }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!f.name || !f.email || !f.password) { toast.error("Fill all fields"); return; }
    setSaving(true);
    try { await api.post("/crm/users", f); toast.success("Team member added"); setF({ name: "", email: "", password: "", role: "agent" }); load(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not add user"); }
    finally { setSaving(false); }
  };
  const remove = async (id) => { await api.delete(`/crm/users/${id}`); toast.success("Removed"); load(); };
  return (
    <CenterModal onClose={onClose}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl flex items-center gap-2"><Users className="h-5 w-5 text-gold" /> Team</h2>
        <button onClick={onClose} className="text-[#A3A3A3] hover:text-white"><X className="h-5 w-5" /></button>
      </div>
      <div className="space-y-2 mb-6">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between border border-white/10 px-3 py-2" data-testid={`team-user-${u.id}`}>
            <div>
              <p className="text-sm">{u.name} <span className="text-[10px] font-mono uppercase text-gold ml-1">{u.role}</span></p>
              <p className="text-xs text-[#A3A3A3]">{u.email}</p>
            </div>
            {u.id !== me?.id && <button onClick={() => remove(u.id)} className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
      </div>
      <p className="font-mono text-[11px] tracking-widest uppercase text-[#A3A3A3] mb-3">Add team member</p>
      <div className="space-y-3">
        <input className={input} placeholder="Name" value={f.name} onChange={up("name")} data-testid="team-name" />
        <input className={input} placeholder="Email" value={f.email} onChange={up("email")} data-testid="team-email" />
        <input className={input} type="password" placeholder="Temporary password" value={f.password} onChange={up("password")} data-testid="team-password" />
        <select className={`${input} cursor-pointer`} value={f.role} onChange={up("role")} data-testid="team-role">
          <option value="agent" className="bg-[#0d0d0d]">Agent</option>
          <option value="admin" className="bg-[#0d0d0d]">Admin</option>
        </select>
        <button onClick={add} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold py-3 hover:bg-gold-light transition-colors disabled:opacity-60" data-testid="team-add">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Add Member
        </button>
      </div>
    </CenterModal>
  );
}

const APPT_STATUS = {
  requested: { label: "Requested", color: "#60a5fa" },
  confirmed: { label: "Confirmed", color: "#34d399" },
  completed: { label: "Completed", color: "#a3a3a3" },
  cancelled: { label: "Cancelled", color: "#ef4444" },
};
const APPT_KEYS = Object.keys(APPT_STATUS);

function Appointments() {
  const [appts, setAppts] = useState([]);
  const [users, setUsers] = useState([]);
  const [calUrl, setCalUrl] = useState(null);
  const [showSync, setShowSync] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [reschedId, setReschedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [a, u, c] = await Promise.all([
        api.get("/crm/appointments"),
        api.get("/crm/users"),
        api.get("/crm/calendar-url"),
      ]);
      setAppts(a.data); setUsers(u.data); setCalUrl(c.data);
    } catch (e) {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    const { data } = await api.patch(`/crm/appointments/${id}`, body);
    setAppts((prev) => prev.map((a) => (a.id === id ? data : a)));
    toast.success("Updated");
  };
  const del = async (id) => {
    await api.delete(`/crm/appointments/${id}`);
    setAppts((prev) => prev.filter((a) => a.id !== id));
    toast.success("Appointment removed");
  };
  const reschedule = async (id, date, slot) => { await patch(id, { date, slot }); setReschedId(null); };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

  return (
    <div data-testid="appointments-view">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl">Appointments</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">{appts.length} scheduled visit{appts.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowSync((v) => !v)} className="inline-flex items-center gap-2 border border-gold text-gold px-5 py-2.5 text-sm hover:bg-gold hover:text-black transition-colors" data-testid="sync-iphone-btn">
            <Smartphone className="h-4 w-4" /> <span className="hidden sm:inline">Sync to iPhone Calendar</span>
          </button>
          <button onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 bg-gold text-black font-semibold px-5 py-2.5 text-sm hover:bg-gold-light transition-colors" data-testid="new-appt-btn">
            <Plus className="h-4 w-4" /> New Appointment
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showNew && <NewAppointmentModal users={users} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      </AnimatePresence>

      {showSync && calUrl && (
        <div className="mb-8 border border-white/10 bg-[#0a0a0a] p-6" data-testid="sync-panel">
          <p className="font-display text-xl mb-2">Add all appointments to your iPhone</p>
          <ol className="text-sm text-[#A3A3A3] space-y-1 mb-4 list-decimal list-inside">
            <li>On your iPhone: <span className="text-[#F5F5F0]">Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar</span>.</li>
            <li>Paste the <span className="text-gold">HTTPS link</span> below as the Server, then Next → Save.</li>
            <li>Leave <span className="text-[#F5F5F0]">Use SSL = ON</span>. If it says "Cannot Connect Using SSL", make sure you used the https link (not webcal) — then tap Save again.</li>
            <li>New bookings appear automatically within ~30 minutes.</li>
          </ol>
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#5a5a5a] mb-1">Recommended — HTTPS (paste in Settings)</p>
          <div className="flex items-center gap-2 bg-[#0d0d0d] border border-white/10 px-3 py-2 mb-4">
            <code className="text-xs text-gold break-all flex-1" data-testid="sync-https-url">{calUrl.https}</code>
            <button onClick={() => copy(calUrl.https)} className="text-[#A3A3A3] hover:text-gold shrink-0" data-testid="sync-copy-https"><Copy className="h-4 w-4" /></button>
          </div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#5a5a5a] mb-1">Or, on your phone, tap to add instantly</p>
          <a href={calUrl.webcal} className="inline-flex items-center gap-2 border border-gold text-gold px-4 py-2 text-sm hover:bg-gold hover:text-black transition-colors" data-testid="sync-webcal-link">
            <CalendarPlus className="h-4 w-4" /> Tap to subscribe (open on iPhone)
          </a>
          <p className="text-xs text-[#5a5a5a] mt-3">Note: SSL works reliably on your published/live domain. On the temporary preview link some devices show the SSL prompt — tap Continue, or publish first for a clean connection.</p>
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center text-[#A3A3A3]"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : appts.length === 0 ? (
        <div className="py-20 text-center border border-white/10 bg-[#0a0a0a]"><p className="text-[#A3A3A3]">No appointments booked yet.</p></div>
      ) : (
        <div className="space-y-3" data-testid="appointments-list">
          {appts.map((a) => {
            const st = APPT_STATUS[a.status] || APPT_STATUS.requested;
            return (
              <div key={a.id} className="border border-white/10 bg-[#0a0a0a] p-5" data-testid={`appt-${a.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-2 text-gold font-mono text-sm"><CalendarDays className="h-4 w-4" /> {a.date}</span>
                      <span className="inline-flex items-center gap-2 text-[#F5F5F0] font-mono text-sm"><Clock className="h-4 w-4 text-gold" /> {a.slot_label}</span>
                      <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider" style={{ color: st.color, border: `1px solid ${st.color}44` }}>{st.label}</span>
                    </div>
                    <p className="font-display text-2xl mt-2">{a.name}</p>
                    <div className="text-sm text-[#A3A3A3] mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1 hover:text-gold"><Phone className="h-3 w-3 text-gold" /> {a.phone}</a>
                      {a.email && <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1 hover:text-gold"><Mail className="h-3 w-3 text-gold" /> {a.email}</a>}
                      {a.service && <span>{a.service}</span>}
                      {a.address && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-gold" /> {a.address}</span>}
                    </div>
                    {a.message && <p className="text-sm text-[#A3A3A3] mt-2 border border-white/10 p-2 bg-black/30">{a.message}</p>}
                  </div>

                  <div className="flex flex-col gap-2 lg:items-end shrink-0">
                    <select value={a.status} onChange={(e) => patch(a.id, { status: e.target.value })} data-testid={`appt-status-${a.id}`}
                      className="bg-[#0d0d0d] border border-white/10 px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-gold cursor-pointer">
                      {APPT_KEYS.map((k) => <option key={k} value={k} className="bg-[#0d0d0d]">{APPT_STATUS[k].label}</option>)}
                    </select>
                    <select value={a.assigned_to || ""} onChange={(e) => patch(a.id, { assigned_to: e.target.value })} data-testid={`appt-assign-${a.id}`}
                      className="bg-[#0d0d0d] border border-white/10 px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-gold cursor-pointer">
                      <option value="" className="bg-[#0d0d0d]">Unassigned</option>
                      {users.map((u) => <option key={u.id} value={u.id} className="bg-[#0d0d0d]">{u.name}</option>)}
                    </select>
                    <div className="flex items-center gap-3 mt-1">
                      <button onClick={() => setReschedId(reschedId === a.id ? null : a.id)} className="inline-flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-gold" data-testid={`appt-reschedule-${a.id}`}>
                        <CalendarDays className="h-4 w-4" /> Reschedule
                      </button>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/appointments/${a.id}/calendar.ics`} className="inline-flex items-center gap-1 text-xs text-gold hover:text-gold-light" data-testid={`appt-ics-${a.id}`}>
                        <CalendarPlus className="h-4 w-4" /> Add to Calendar
                      </a>
                      <button onClick={() => del(a.id)} className="text-red-400 hover:text-red-300" data-testid={`appt-delete-${a.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
                {reschedId === a.id && <RescheduleBox appt={a} onSave={(date, slot) => reschedule(a.id, date, slot)} onCancel={() => setReschedId(null)} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NewAppointmentModal({ users, onClose, onCreated }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ name: "", phone: "", email: "", service: "", address: "", message: "", date: todayStr, slot: "", assigned_to: "" });
  const [slots, setSlots] = useState([]);
  const [saving, setSaving] = useState(false);
  const up = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    let active = true;
    api.get(`/availability?date=${f.date}`).then(({ data }) => { if (active) setSlots(data.slots); }).catch(() => setSlots([]));
    return () => { active = false; };
  }, [f.date]);

  const save = async () => {
    if (!f.name || !f.phone || !f.email || !f.slot) { toast.error("Name, phone, email and time are required"); return; }
    setSaving(true);
    try { await api.post("/crm/appointments", f); toast.success("Appointment created"); onCreated(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not create"); }
    finally { setSaving(false); }
  };

  return (
    <CenterModal onClose={onClose}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl flex items-center gap-2"><CalendarDays className="h-5 w-5 text-gold" /> New Appointment</h2>
        <button onClick={onClose} className="text-[#A3A3A3] hover:text-white"><X className="h-5 w-5" /></button>
      </div>
      <div className="space-y-3" data-testid="new-appt-form">
        <input className={input} placeholder="Full name *" value={f.name} onChange={up("name")} data-testid="na-name" />
        <div className="grid grid-cols-2 gap-3">
          <input className={input} placeholder="Phone *" value={f.phone} onChange={up("phone")} data-testid="na-phone" />
          <input className={input} placeholder="Email *" value={f.email} onChange={up("email")} data-testid="na-email" />
        </div>
        <input className={input} placeholder="Service" value={f.service} onChange={up("service")} data-testid="na-service" />
        <input className={input} placeholder="Property address" value={f.address} onChange={up("address")} data-testid="na-address" />
        <div className="grid grid-cols-2 gap-3">
          <input type="date" min={todayStr} className={`${input} cursor-pointer`} value={f.date} onChange={(e) => setF((p) => ({ ...p, date: e.target.value, slot: "" }))} data-testid="na-date" />
          <select className={`${input} cursor-pointer`} value={f.slot} onChange={up("slot")} data-testid="na-slot">
            <option value="" className="bg-[#0d0d0d]">Pick a time</option>
            {slots.map((s) => <option key={s.start} value={s.start} disabled={!s.available} className="bg-[#0d0d0d]">{s.label}{!s.available ? " (booked)" : ""}</option>)}
          </select>
        </div>
        <select className={`${input} cursor-pointer`} value={f.assigned_to} onChange={up("assigned_to")} data-testid="na-assign">
          <option value="" className="bg-[#0d0d0d]">Assign to (optional)</option>
          {users.map((u) => <option key={u.id} value={u.id} className="bg-[#0d0d0d]">{u.name}</option>)}
        </select>
        <textarea rows={2} className={`${input} resize-none`} placeholder="Notes" value={f.message} onChange={up("message")} data-testid="na-message" />
        <button onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold py-3 hover:bg-gold-light transition-colors disabled:opacity-60" data-testid="na-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create Appointment
        </button>
      </div>
    </CenterModal>
  );
}

function RescheduleBox({ appt, onSave, onCancel }) {
  const [date, setDate] = useState(appt.date);
  const [slot, setSlot] = useState(appt.slot);
  const [slots, setSlots] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let active = true;
    api.get(`/availability?date=${date}`).then(({ data }) => { if (active) setSlots(data.slots); }).catch(() => setSlots([]));
    return () => { active = false; };
  }, [date]);
  const save = async () => { if (!slot) { toast.error("Pick a time"); return; } setSaving(true); await onSave(date, slot); setSaving(false); };
  return (
    <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap items-end gap-3" data-testid={`reschedule-box-${appt.id}`}>
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-[#A3A3A3] mb-1">New date</label>
        <input type="date" min={new Date().toISOString().slice(0, 10)} value={date} onChange={(e) => { setDate(e.target.value); setSlot(""); }}
          className="bg-[#0d0d0d] border border-white/10 px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-gold cursor-pointer" data-testid={`reschedule-date-${appt.id}`} />
      </div>
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-[#A3A3A3] mb-1">New time</label>
        <select value={slot} onChange={(e) => setSlot(e.target.value)}
          className="bg-[#0d0d0d] border border-white/10 px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-gold cursor-pointer" data-testid={`reschedule-slot-${appt.id}`}>
          <option value="" className="bg-[#0d0d0d]">Pick a time</option>
          {slots.map((s) => <option key={s.start} value={s.start} disabled={!s.available && s.start !== appt.slot} className="bg-[#0d0d0d]">{s.label}{(!s.available && s.start !== appt.slot) ? " (booked)" : ""}</option>)}
        </select>
      </div>
      <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-gold text-black font-semibold px-5 py-2 text-sm hover:bg-gold-light transition-colors disabled:opacity-60" data-testid={`reschedule-save-${appt.id}`}>
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save New Time"}
      </button>
      <button onClick={onCancel} className="px-4 py-2 text-sm text-[#A3A3A3] hover:text-white" data-testid={`reschedule-cancel-${appt.id}`}>Cancel</button>
    </div>
  );
}

const AI_PRIORITY = {
  emergency: { label: "Emergency", color: "#ef4444" },
  hot: { label: "Hot", color: "#f59e0b" },
  routine: { label: "Routine", color: "#60a5fa" },
};
function AiBadge({ priority }) {
  const p = AI_PRIORITY[priority] || AI_PRIORITY.routine;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5" style={{ color: p.color, border: `1px solid ${p.color}55` }} data-testid={`ai-badge-${priority}`}>
      <Sparkles className="h-2.5 w-2.5" /> AI: {p.label}
    </span>
  );
}
